import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from "react";
import type { RouteObject } from "react-router";
import { CONSOLE_PATHS, consoleRoutes } from "@sagot/constants";
import { SERVICE_AREAS, type ServiceAreaId } from "./areas";
import { fieldRoutes } from "./field/field-routes";
import { ServiceAreaSkeleton } from "./service-area-skeleton";
import { TYPE_EDITOR_PATH } from "./settings/settings-constants";

const ServiceAreaPage = lazy(() => import("./service-area-page").then((module) => ({ default: module.ServiceAreaPage })));

const AssetsRoute = lazy(() => import("./assets/assets-route").then((module) => ({ default: module.AssetsRoute })));
const InstallationsRoute = lazy(() => import("./installations/installations-route").then((module) => ({ default: module.InstallationsRoute })));
const VisitsRoute = lazy(() => import("./visits/visits-route").then((module) => ({ default: module.VisitsRoute })));
const ContractsRoute = lazy(() => import("./contracts/contracts-route").then((module) => ({ default: module.ContractsRoute })));
const SegmentsRoute = lazy(() => import("./segments/segments-route").then((module) => ({ default: module.SegmentsRoute })));
const SparesRoute = lazy(() => import("./spares/spares-route").then((module) => ({ default: module.SparesRoute })));
const OrganisationsRoute = lazy(() => import("./organisations/organisations-route").then((module) => ({ default: module.OrganisationsRoute })));
const ContactsRoute = lazy(() => import("./contacts/contacts-route").then((module) => ({ default: module.ContactsRoute })));
const TicketsRoute = lazy(() => import("./tickets/tickets-route").then((module) => ({ default: module.TicketsRoute })));
const ReportsRoute = lazy(() => import("./reports/reports-route").then((module) => ({ default: module.ReportsRoute })));
const OrdersRoute = lazy(() => import("./orders/orders-route").then((module) => ({ default: module.OrdersRoute })));
const CatalogueRoute = lazy(() => import("./catalogue/catalogue-route").then((module) => ({ default: module.CatalogueRoute })));
const ServiceSettingsRoute = lazy(() => import("./settings/service-settings-route").then((module) => ({ default: module.ServiceSettingsRoute })));
const TypeEditorRoute = lazy(() => import("./settings/type-editor-route").then((module) => ({ default: module.TypeEditorRoute })));

/** Pages whose own screen has shipped (each mounts its own `ServiceAreaGuard`); every other page still opens its shell. */
const AREA_SCREENS: Partial<Record<ServiceAreaId, LazyExoticComponent<ComponentType>>> = {
  tickets: TicketsRoute,
  organisations: OrganisationsRoute,
  contacts: ContactsRoute,
  assets: AssetsRoute,
  installations: InstallationsRoute,
  visits: VisitsRoute,
  orders: OrdersRoute,
  catalogue: CatalogueRoute,
  "service-reports": ReportsRoute,
  "service-settings": ServiceSettingsRoute,
};

function ServiceAreaRoute({ id }: { id: ServiceAreaId }) {
  const Screen = AREA_SCREENS[id];
  return <Suspense fallback={<ServiceAreaSkeleton />}>{Screen ? <Screen /> : <ServiceAreaPage id={id} />}</Suspense>;
}

/** The ticket type editor (C18) is a page of Service settings with its own address; it sits behind the same guard. */
const typeEditorRoute: RouteObject = {
  path: TYPE_EDITOR_PATH,
  element: (
    <Suspense fallback={<ServiceAreaSkeleton />}>
      <TypeEditorRoute />
    </Suspense>
  ),
};

/** Service contracts (C08) are a tab of Assets with their own address; the sidebar keeps Assets active and the crumbs name Contracts. */
const contractsRoute: RouteObject = {
  path: CONSOLE_PATHS.contracts,
  handle: { titleKey: "service.contracts.title", parent: { labelKey: "service.areas.assets.title", to: (ws: string) => consoleRoutes.assets(ws) } },
  element: (
    <Suspense fallback={<ServiceAreaSkeleton />}>
      <ContractsRoute />
    </Suspense>
  ),
};

/** Spare-part requests (AST-092) are a tab of Assets with their own address, like Contracts. */
const sparesRoute: RouteObject = {
  path: CONSOLE_PATHS.spares,
  handle: { titleKey: "service.spares.title", parent: { labelKey: "service.areas.assets.title", to: (ws: string) => consoleRoutes.assets(ws) } },
  element: (
    <Suspense fallback={<ServiceAreaSkeleton />}>
      <SparesRoute />
    </Suspense>
  ),
};

/** Customer segments (CRM-005) are a page of Contacts with their own address; the crumbs name Contacts. */
const segmentsRoute: RouteObject = {
  path: CONSOLE_PATHS.segments,
  handle: { titleKey: "segments.list.crumb", parent: { labelKey: "service.areas.contacts.title", to: (ws: string) => consoleRoutes.contacts(ws) } },
  element: (
    <Suspense fallback={<ServiceAreaSkeleton />}>
      <SegmentsRoute />
    </Suspense>
  ),
};

/** The ten Service routes (scope 16.1), lazy so the shells add nothing to the console's first load. Each page renders inside `ServiceAreaGuard`. */
export const serviceRoutes: RouteObject[] = [...SERVICE_AREAS.map((area) => ({ path: area.path, element: <ServiceAreaRoute id={area.id} /> })), typeEditorRoute, contractsRoute, sparesRoute, segmentsRoute, ...fieldRoutes];
